import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { History, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader } from "@/components/Loader";

function VersionRow({ v, onActivate }) {
  const blocked = (v.blocked || []).length > 0;
  return (
    <li className="rounded-xl border border-[#27272A] bg-[#121215] p-4" data-testid={`version-${v.id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-[#F4F4F5]">Settimana {v.week} · {v.content_version}</p>
        {v.active ? (
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#10B981]" data-testid={`version-active-${v.id}`}><CheckCircle2 size={14} /> In uso</span>
        ) : blocked ? (
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#F87171]"><AlertTriangle size={14} /> Bloccata</span>
        ) : (
          <button onClick={() => onActivate(v)} data-testid={`version-activate-${v.id}`} className="rounded-full border border-[#D4AF37] px-3 py-1 text-sm font-semibold text-[#E5C158]">Usa questa versione</button>
        )}
      </div>
      <p className="mt-1 text-sm text-[#8A8E96]">
        {new Date(v.created_at).toLocaleString("it-IT")} · testi cambiati: {v.changed_fields} · giornate iniziate con questa versione: {v.days_recorded}
      </p>
      {blocked && <p className="mt-2 text-sm text-[#F87171]">Struttura cambiata, serve un piano di migrazione: {v.blocked.slice(0, 3).join("; ")}</p>}
    </li>
  );
}

export default function AdminVersions() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const load = () => api.get("/admin/c2g/versions").then(({ data }) => setList(data.versions)).catch(() => navigate("/admin", { replace: true }));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const activate = async (v) => {
    if (!window.confirm(`Usare la versione ${v.content_version} della Settimana ${v.week}? I progressi dei clienti non cambiano.`)) return;
    try {
      await api.post(`/admin/c2g/versions/${v.id}/activate`);
      toast.success("Versione attivata");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Non riuscito");
    }
  };

  if (!list) return <Loader />;
  return (
    <AppShell title="Versioni dei testi" greeting="Admin">
      <div className="px-5 pt-6" data-testid="admin-versions-page">
        <p className="flex items-start gap-2 text-sm leading-relaxed text-[#B4B4BC]">
          <History size={16} className="mt-0.5 shrink-0 text-[#D4AF37]" />
          Le correzioni ai soli testi si applicano subito a tutti, senza toccare passi scelti, stati e statistiche. Se una nuova versione cambia la struttura (giorni, passi, identificativi), resta bloccata.
        </p>
        <ul className="mt-5 space-y-2">{list.map((v) => <VersionRow key={v.id} v={v} onActivate={activate} />)}</ul>
      </div>
    </AppShell>
  );
}
